import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, IconButton, Tooltip, Collapse, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from "tss-react/mui";
import GeneralSection from '../Components/PagesComponents/Settings/General/GeneralSection';
import RolePermissions from '../Components/PagesComponents/Settings/UserRoleManagement/RolePermissions';
import ButtonContained from '../Components/Common/ButtonContained';
import StageOfClaims from '../Components/PagesComponents/Settings/StageOfClaims';
import Account from '../Components/PagesComponents/Settings/Account/Account';
import Faq from '../Components/PagesComponents/Settings/Faq/Faq';
import Banner from '../Components/PagesComponents/Settings/Banner/Banner';
import GeneralNotification from '../Components/PagesComponents/Settings/GeneralNotification/GeneralNotification';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { showLoader } from '../Redux/Slices/loaderSlice';
import { getAvailableAccess } from '../Redux/Actions/action';
import { checkAccess } from '../Utils/handler';
import Occupation from '../Components/PagesComponents/Settings/Occupation/Occupation';
import AppVersion from '../Components/PagesComponents/Settings/AppVersion/AppVersion';
import { useSearchParams } from 'react-router-dom';
import AppRolePermission from '../Components/PagesComponents/Settings/AppRolePermission/AppRolePermission';
import ConfigSetting from '../Components/PagesComponents/Settings/ConfigSetting/ConfigSetting';
import PpskId from '../Components/PagesComponents/Settings/PpskId/PpskId';
import SmsGateway from '../Components/PagesComponents/Settings/SmsGateway/SmsGateway';
import AgeWisePayment from '../Components/PagesComponents/Settings/AgeWisePayment/AgeWisePayment';
import PageManagement from '../Components/PagesComponents/Settings/PageManagement/PageManagement';
import StageManagement from '../Components/PagesComponents/Settings/StageManagement/StageManagement';
import ReferenceTracking from '../Components/PagesComponents/Settings/ReferenceTracking/ReferenceTracking';
import SystemManagement from '../Components/PagesComponents/Settings/SystemManagement/SystemManagement';
import WebappUpload from '../Components/PagesComponents/Settings/WebappUpload/WebappUpload';
// Icons
import SettingsIcon from '@mui/icons-material/Settings';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ImageIcon from '@mui/icons-material/Image';
import GroupIcon from '@mui/icons-material/Group';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BadgeIcon from '@mui/icons-material/Badge';
import TuneIcon from '@mui/icons-material/Tune';
import SmsIcon from '@mui/icons-material/Sms';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import HelpIcon from '@mui/icons-material/Help';
import WorkIcon from '@mui/icons-material/Work';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SecurityIcon from '@mui/icons-material/Security';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LayersIcon from '@mui/icons-material/Layers';
import ShareIcon from '@mui/icons-material/Share';
import BuildIcon from '@mui/icons-material/Build';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

const useStyles = makeStyles()((theme) => {
  return {
    verticalTab: {
      '&.MuiTabs-vertical ': {
        borderRight: '0px solid'
      },
      '& .MuiTab-root ': {
        backgroundColor: "#fff",
        textTransform: 'capitalize',
        alignItems: 'flex-start',
        marginBottom: '1px',
        borderRadius: '0px',
      },
      '& .MuiTab-root.Mui-selected ': {
        backgroundColor: '#3E4094',
        color: '#fff'
      },
      '& .MuiTabs-indicator ': {
        width: '0px'
      }
    },
    tab: {
      textAlign: 'left',
      fontWeight: 500,

      borderRadius: '4px',
      marginBottom: '8px',
    }
  };
});

// Tab configuration with icons
const TAB_CONFIG = [
  { Component: GeneralSection, accessKey: 'General', icon: <SettingsIcon /> },
  { Component: Banner, accessKey: 'Banner', icon: <ImageIcon /> },
  { Component: RolePermissions, accessKey: 'User & Role Management', icon: <GroupIcon /> },
  { Component: StageOfClaims, accessKey: 'Stage Of Claims', icon: <AccountTreeIcon /> },
  { Component: PpskId, accessKey: 'PPSK ID', icon: <BadgeIcon /> },
  { Component: ConfigSetting, accessKey: 'Config Setting', icon: <TuneIcon /> },
  { Component: SmsGateway, accessKey: 'SMS Gateway', icon: <SmsIcon /> },
  { Component: GeneralNotification, accessKey: 'Reminder Settings', icon: <NotificationsIcon /> },
  { Component: AgeWisePayment, accessKey: 'Age Wise Payment Settings', icon: <PaymentsIcon /> },
  { Component: Account, accessKey: 'Account', icon: <AccountCircleIcon /> },
  { Component: Faq, accessKey: 'FAQ', icon: <HelpIcon /> },
  { Component: Occupation, accessKey: 'Occupations', icon: <WorkIcon /> },
  { Component: AppVersion, accessKey: 'App Version', icon: <PhoneAndroidIcon /> },
  { Component: AppRolePermission, accessKey: 'App Role & Permission', icon: <SecurityIcon /> },
  { Component: PageManagement, accessKey: 'Page Management', icon: <ViewModuleIcon /> },
  { Component: StageManagement, accessKey: 'Stage Management', icon: <LayersIcon /> },
  { Component: ReferenceTracking, accessKey: 'Reference Tracking', icon: <ShareIcon /> },
  { Component: SystemManagement, accessKey: 'System Management', icon: <BuildIcon /> },
  { Component: WebappUpload, accessKey: 'Web App Upload', icon: <PhoneIphoneIcon /> },
];

const Settings = () => {
  // ---------- Hook -----------
  const { classes } = useStyles();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const dataAccess = useSelector((state) => state?.allAvailableAccess?.AllAvailableAccessList);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Number(searchParams.get("tab")) || 0;

  // --------------- State -------------
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [showMenu, setShowMenu] = useState(true);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Memoize accessible tabs to prevent recalculation on every render
  const accessibleTabs = useMemo(() => 
    TAB_CONFIG.filter((tab) => checkAccess(pathname, dataAccess, tab?.accessKey)),
    [pathname, dataAccess]
  );

  // --------------- handleChange ---------------
  const handleTabSelect = useCallback((index) => {
    setSelectedTab(index);
    setSearchParams({ tab: index.toString() });
    if (isMdDown) {
      setShowMenu(false);
    }
  }, [isMdDown, setSearchParams]);

  const toggleMenu = useCallback(() => {
    setShowMenu((prev) => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarExpanded((prev) => !prev);
  }, []);

  // Render the selected tab content
  const renderTabContent = useMemo(() => {
    const currentTab = accessibleTabs[selectedTab];
    if (!currentTab) return null;
    
    if (currentTab.Component) {
      const TabComponent = currentTab.Component;
      return <TabComponent />;
    }
    
    return (
      <Box>
        <Typography>{currentTab.placeholder}</Typography>
      </Box>
    );
  }, [accessibleTabs, selectedTab]);

  // ---------- useEffect ------------ 
  useEffect(() => {
    dispatch(showLoader(true));
    dispatch(getAvailableAccess())
      .unwrap()
      .catch((error) => console.error("Error fetching access data", error))
      .finally(() => dispatch(showLoader(false)));
  }, [dispatch]);

  return (
    <Grid container spacing={2}>
      {/* Title Section */}
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: { lg: '22px', md: '18px' }, fontWeight: 600 }}>
          Settings
        </Typography>
        {isMdDown && (
          <ButtonContained
            height="30px"
            text={showMenu ? "Show Content" : "View Menu"}
            onClick={toggleMenu}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', minHeight: '70vh' }}>
            {/* Collapsible Sidebar */}
            {(showMenu || !isMdDown) && (
              <Box
                sx={{
                  width: sidebarExpanded ? 260 : 64,
                  minWidth: sidebarExpanded ? 260 : 64,
                  transition: 'all 0.3s ease',
                  borderRight: '1px solid #e2e8f0',
                  backgroundColor: '#fafafa',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Collapse Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: sidebarExpanded ? 'flex-end' : 'center',
                    p: 1,
                    borderBottom: '1px solid #e2e8f0',
                  }}
                >
                  <IconButton
                    onClick={toggleSidebar}
                    size="small"
                    sx={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      '&:hover': { backgroundColor: '#f1f5f9' },
                    }}
                  >
                    {sidebarExpanded ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                  </IconButton>
                </Box>

                {/* Menu Items */}
                <List sx={{ px: 1, py: 1, flexGrow: 1, overflow: 'auto' }}>
                  {accessibleTabs?.map((tab, index) => (
                    <Tooltip
                      key={tab?.accessKey}
                      title={!sidebarExpanded ? tab?.accessKey : ''}
                      placement="right"
                      arrow
                    >
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          selected={selectedTab === index}
                          onClick={() => handleTabSelect(index)}
                          sx={{
                            borderRadius: '10px',
                            minHeight: 44,
                            px: sidebarExpanded ? 2 : 1.5,
                            justifyContent: sidebarExpanded ? 'initial' : 'center',
                            '&.Mui-selected': {
                              backgroundColor: '#3E4094',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: '#363785',
                              },
                              '& .MuiListItemIcon-root': {
                                color: '#fff',
                              },
                            },
                            '&:hover': {
                              backgroundColor: '#e2e8f0',
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: sidebarExpanded ? 2 : 0,
                              justifyContent: 'center',
                              color: selectedTab === index ? '#fff' : '#64748b',
                            }}
                          >
                            {tab?.icon}
                          </ListItemIcon>
                          <Collapse in={sidebarExpanded} orientation="horizontal">
                            <ListItemText
                              primary={tab?.accessKey}
                              sx={{
                                opacity: sidebarExpanded ? 1 : 0,
                                whiteSpace: 'nowrap',
                                '& .MuiTypography-root': {
                                  fontSize: '14px',
                                  fontWeight: selectedTab === index ? 600 : 500,
                                },
                              }}
                            />
                          </Collapse>
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
              </Box>
            )}

            {/* Tab Content */}
            {(!showMenu || !isMdDown) && (
              <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto', backgroundColor: '#fff' }}>
                {renderTabContent}
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Settings;
